import {
  BACKFIRE_DEFAULT_BULLET_AMOUNT,
  BACKFIRE_DEFAULT_MODE,
  BACKFIRE_MAX_BULLETS,
  BACKFIRE_MIN_BULLETS,
  BackfireMode,
  BackfireSettings,
  DEFAULT_BACKFIRE_SETTINGS,
} from "../constants/backfire";

export const clampBackfireBulletAmount = (value: number): number =>
  Math.min(
    BACKFIRE_MAX_BULLETS,
    Math.max(BACKFIRE_MIN_BULLETS, Math.floor(value))
  );

export const normalizeBackfireSettings = (
  raw: Partial<BackfireSettings> | null | undefined
): BackfireSettings => {
  if (!raw) {
    return { ...DEFAULT_BACKFIRE_SETTINGS };
  }

  const modeValues = Object.values(BackfireMode).filter(
    (value) => typeof value === "number"
  ) as BackfireMode[];

  // Legacy localStorage used None = 5 before aligning with on-chain BackfirePolicy.
  const rawMode = Number(raw.mode) === 5 ? BackfireMode.None : raw.mode;

  const mode = modeValues.includes(rawMode as BackfireMode)
    ? (rawMode as BackfireMode)
    : BACKFIRE_DEFAULT_MODE;

  if (mode === BackfireMode.Range) {
    const bulletAmount = clampBackfireBulletAmount(
      Number(raw.rangeMin ?? raw.rangeMax ?? BACKFIRE_DEFAULT_BULLET_AMOUNT)
    );

    return { mode, rangeMin: bulletAmount, rangeMax: bulletAmount };
  }

  const rangeMin = clampBackfireBulletAmount(
    Number(raw.rangeMin ?? BACKFIRE_DEFAULT_BULLET_AMOUNT)
  );
  let rangeMax = clampBackfireBulletAmount(
    Number(raw.rangeMax ?? BACKFIRE_DEFAULT_BULLET_AMOUNT)
  );

  if (rangeMin > rangeMax) {
    rangeMax = rangeMin;
  }

  return { mode, rangeMin, rangeMax };
};

export const parseStoredBackfireSettings = (
  stored: string | null
): BackfireSettings => {
  if (!stored) {
    return { ...DEFAULT_BACKFIRE_SETTINGS };
  }

  try {
    return normalizeBackfireSettings(JSON.parse(stored) as Partial<BackfireSettings>);
  } catch {
    return { ...DEFAULT_BACKFIRE_SETTINGS };
  }
};

export const validateBackfireSettings = (
  settings: BackfireSettings
): string | null => {
  const { mode, rangeMin } = settings;

  if (mode === BackfireMode.Range) {
    if (
      rangeMin < BACKFIRE_MIN_BULLETS ||
      rangeMin > BACKFIRE_MAX_BULLETS
    ) {
      return `Bullet amount must be between ${BACKFIRE_MIN_BULLETS.toLocaleString()} and ${BACKFIRE_MAX_BULLETS.toLocaleString()}.`;
    }
  }

  return null;
};
